import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { updateProfile } from 'firebase/auth';
import { storage, db } from './firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import './PersonalCenter.css';

function PersonalCenter({
  user,
  categories,
  completedCounts,
  words,
  onBack,
  getCumulativeMasteredWords,
  userProfile,
  setUserProfile,
  onResetCategory,
}) {
  const [newDisplayName, setNewDisplayName] = useState(userProfile.displayName || '');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [masteredStats, setMasteredStats] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [releasingWord, setReleasingWord] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resettingCategory, setResettingCategory] = useState(null);
  
  // 排行榜相关状态
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentUserRank, setCurrentUserRank] = useState(null);
  const [isLoadingLeaderboard, setIsLoadingLeaderboard] = useState(false);

  // 修复：正确计算每个类别的单词数量
  const getWordsForCategory = useCallback((categoryValue) => {
    return words.filter((w) => {
      if (Array.isArray(w.category)) {
        return w.category.some(cat => 
          String(cat).toLowerCase().trim() === categoryValue.toLowerCase().trim()
        );
      }
      return String(w.category || '').toLowerCase().trim() === categoryValue.toLowerCase().trim();
    });
  }, [words]);

  // 计算总体统计数据（移到最前面，解决依赖问题）
  const totalMastered = useMemo(() => {
    const allMasteredWords = new Set();
    
    Object.values(masteredStats).forEach(stat => {
      // 只统计手动削词
      (stat.manualMasteredWords || []).forEach(word => allMasteredWords.add(word));
    });
    
    return allMasteredWords.size;
  }, [masteredStats]);
  
  const totalWords = useMemo(() => {
    return Object.values(masteredStats).reduce((acc, stat) => 
      acc + (stat.totalWords || 0), 0
    );
  }, [masteredStats]);

  const overallProgress = useMemo(() => {
    return totalWords > 0 ? Math.round((totalMastered / totalWords) * 100) : 0;
  }, [totalWords, totalMastered]);

  // 计算用户等级
  const calculateLevel = useCallback((masteredCount) => {
    return Math.floor(masteredCount / 100) + 1;
  }, []);

  // 获取等级称号
  const getLevelTitle = useCallback((level) => {
    if (level >= 100) return '传奇大师';
    if (level >= 80) return '至尊王者';
    if (level >= 60) return '钻石宗师';
    if (level >= 40) return '铂金精英';
    if (level >= 20) return '黄金学者';
    if (level >= 10) return '白银勇者';
    return '青铜学徒';
  }, []);

  // 获取每个类别的削词统计和详细单词
  useEffect(() => {
    const fetchMasteredStats = async () => {
      try {
        const stats = {};
        for (const cat of categories) {
          try {
            const { manualMastered } = await getCumulativeMasteredWords(cat.value);
            const totalWordsInCategory = getWordsForCategory(cat.value).length;
            
            stats[cat.value] = {
              manualMasteredCount: Object.keys(manualMastered).length,
              totalWords: totalWordsInCategory,
              manualMasteredWords: Object.keys(manualMastered),
              // learnedMastered 数据仍从数据库读取，但不再用于统计
            };
          } catch (err) {
            console.error(`获取${cat.value}削词统计失败:`, err);
            stats[cat.value] = {
              manualMasteredCount: 0,
              learnedMasteredCount: 0,
              totalWords: getWordsForCategory(cat.value).length,
              manualMasteredWords: [],
              learnedMasteredWords: [],
            };
          }
        }
        setMasteredStats(stats);
        console.log('削词统计加载完成:', stats);
      } catch (err) {
        console.error('加载削词统计失败:', err);
        setError('加载学习统计失败，请检查网络连接');
      }
    };

    if (user) {
      fetchMasteredStats();
    }
  }, [user, categories, getCumulativeMasteredWords, getWordsForCategory]);

  // 加载排行榜数据
  useEffect(() => {
    const loadLeaderboard = async () => {
      if (!user) return;
      
      setIsLoadingLeaderboard(true);
      try {
        // 获取所有用户的掌握单词数
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        const userDataPromises = usersSnapshot.docs.map(async (userDoc) => {
          const userId = userDoc.id;
          const userData = userDoc.data();
          
          // 计算该用户的总掌握单词数（去重版本）
          const allMasteredWords = new Set();
          
          for (const cat of categories) {
            try {
              const cumulativeMasteredRef = doc(db, 'users', userId, 'cumulativeMastered', cat.value);
              const cumulativeDoc = await getDoc(cumulativeMasteredRef);
              
              if (cumulativeDoc.exists()) {
                const data = cumulativeDoc.data();
                // 只统计手动削词
                Object.keys(data.manualMastered || {}).forEach(word => allMasteredWords.add(word));
              }
            } catch (err) {
              console.error(`Error loading stats for ${userId}:`, err);
            }
          }
          
          const totalMasteredCount = allMasteredWords.size;
          
          return {
            userId,
            displayName: userData.displayName || '匿名用户',
            photoURL: userData.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.displayName || 'U')}&size=100&background=random`,
            totalMastered: totalMasteredCount,
            level: calculateLevel(totalMasteredCount),
          };
        });
        
        const allUsers = await Promise.all(userDataPromises);
        
        // 按掌握数量排序
        const sorted = allUsers
          .filter(u => u.totalMastered > 0)
          .sort((a, b) => b.totalMastered - a.totalMastered)
          .slice(0, 300);
        
        setLeaderboard(sorted);
        
        // 找到当前用户的排名
        const userIndex = sorted.findIndex(u => u.userId === user.uid);
        if (userIndex !== -1) {
          setCurrentUserRank(userIndex + 1);
        }
        
      } catch (err) {
        console.error('加载排行榜失败:', err);
        setError('加载排行榜失败');
      } finally {
        setIsLoadingLeaderboard(false);
      }
    };
    
    if (user) {  // 移除了 totalMastered >= 0 的条件
      loadLeaderboard();
    }
  }, [user, categories, calculateLevel]); // 移除了 totalMastered 依赖

  // 获取每个类别的削词统计和详细单词
  useEffect(() => {
    const fetchMasteredStats = async () => {
      try {
        const stats = {};
        for (const cat of categories) {
          try {
            const { manualMastered, learnedMastered } = await getCumulativeMasteredWords(cat.value);
            const totalWordsInCategory = getWordsForCategory(cat.value).length;
            
            stats[cat.value] = {
              manualMasteredCount: Object.keys(manualMastered).length,
              learnedMasteredCount: Object.keys(learnedMastered).length,
              totalWords: totalWordsInCategory,
              manualMasteredWords: Object.keys(manualMastered),
              learnedMasteredWords: Object.keys(learnedMastered),
            };
          } catch (err) {
            console.error(`获取${cat.value}削词统计失败:`, err);
            stats[cat.value] = {
              manualMasteredCount: 0,
              learnedMasteredCount: 0,
              totalWords: getWordsForCategory(cat.value).length,
              manualMasteredWords: [],
              learnedMasteredWords: [],
            };
          }
        }
        setMasteredStats(stats);
        console.log('削词统计加载完成:', stats);
      } catch (err) {
        console.error('加载削词统计失败:', err);
        setError('加载学习统计失败，请检查网络连接');
      }
    };

    if (user) {
      fetchMasteredStats();
    }
  }, [user, categories, getCumulativeMasteredWords, getWordsForCategory]);

  // 清理预览URL，防止内存泄漏
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('文件大小不能超过5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('请上传图片文件');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setSuccess(null);
      
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!newDisplayName.trim()) {
      setError('昵称不能为空');
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      let photoURL = userProfile.photoURL;
      
      if (selectedFile) {
        console.log('开始上传头像...');
        const storageRef = ref(storage, `avatars/${user.uid}`);
        const snapshot = await uploadBytes(storageRef, selectedFile);
        photoURL = await getDownloadURL(snapshot.ref);
        console.log('头像上传成功:', photoURL);
      }

      console.log('更新Auth用户资料...');
      await updateProfile(user, {
        displayName: newDisplayName.trim(),
        photoURL,
      });

      console.log('保存用户资料到Firestore...');
      try {
        const userDocRef = doc(db, 'users', user.uid);
        await setDoc(userDocRef, {
          displayName: newDisplayName.trim(),
          photoURL,
          email: user.email,
          lastUpdated: Date.now(),
        }, { merge: true });
        
        console.log('Firestore写入成功');
      } catch (firestoreErr) {
        console.error('Firestore操作失败:', firestoreErr);
        setError('用户资料已更新，但数据同步可能有延迟。');
      }

      setUserProfile({
        displayName: newDisplayName.trim(),
        photoURL,
      });
      
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      
      if (!error) {
        setSuccess('资料更新成功！');
      }
      
    } catch (err) {
      console.error('用户资料更新失败:', err);
      setError(`更新失败: ${err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleResetCategory = async (categoryValue, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 第一步：警告确认
    const firstConfirm = window.confirm(
      `⚠️ 警告：重置将删除 ${categoryValue} 类别的所有学习数据！\n\n这包括：\n- 所有学习进度\n- 手动削词记录\n- 学习掌握记录\n\n此操作不可恢复，请谨慎操作！\n\n点击"确定"继续`
    );
    
    if (!firstConfirm) {
      return;
    }
    
    // 第二步：输入确认
    const userInput = window.prompt(
      `请输入单词 "sure" 来确认重置操作：`
    );
    
    if (userInput !== 'sure') {
      if (userInput !== null) {
        alert('输入错误，重置已取消');
      }
      return;
    }
    
    // 开始加载
    setIsResetting(true);
    setResettingCategory(categoryValue);
    setError(null);
    
    try {
      await onResetCategory(categoryValue);
      
      // 等待一下再刷新数据
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const { manualMastered, learnedMastered } = await getCumulativeMasteredWords(categoryValue);
      setMasteredStats(prev => ({
        ...prev,
        [categoryValue]: {
          ...prev[categoryValue],
          manualMasteredWords: Object.keys(manualMastered),
          learnedMasteredWords: Object.keys(learnedMastered),
          manualMasteredCount: Object.keys(manualMastered).length,
          learnedMasteredCount: Object.keys(learnedMastered).length,
        }
      }));
      
      setSuccess(`${categoryValue} 类别已成功重置`);
    } catch (err) {
      console.error('重置失败:', err);
      setError('重置失败，请重试');
    } finally {
      // 结束加载
      setIsResetting(false);
      setResettingCategory(null);
    }
  };

  // 释放单个已掌握的单词
  const releaseWord = async (categoryValue, word, isMasterType) => {
    if (!window.confirm(`确定要释放单词 "${word}" 吗？释放后可以重新学习该单词。`)) {
      return;
    }
    
    // 开始加载
    setIsReleasing(true);
    setReleasingWord(`${categoryValue}-${word}`);
    setError(null);
    
    try {
      const cumulativeMasteredRef = doc(db, 'users', user.uid, 'cumulativeMastered', categoryValue);
      const docSnap = await getDoc(cumulativeMasteredRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedManualMastered = { ...data.manualMastered };
        const updatedLearnedMastered = { ...data.learnedMastered };
        
        // 根据类型删除对应的单词
        if (isMasterType === 'manual') {
          delete updatedManualMastered[word];
        } else {
          delete updatedLearnedMastered[word];
        }
        
        // 更新数据库
        await setDoc(cumulativeMasteredRef, {
          manualMastered: updatedManualMastered,
          learnedMastered: updatedLearnedMastered,
          lastUpdated: Date.now(),
        });
        
        // 同时清除progress中的记录
        const progressRef = doc(db, 'users', user.uid, 'progress', categoryValue);
        const progressSnap = await getDoc(progressRef);
        if (progressSnap.exists()) {
          const progressData = progressSnap.data();
          const updatedWords = { ...progressData.words };
          delete updatedWords[word];
          
          await setDoc(progressRef, {
            ...progressData,
            words: updatedWords,
            lastUpdated: Date.now(),
          });
        }
        
        // 更新本地状态
        const { manualMastered, learnedMastered } = await getCumulativeMasteredWords(categoryValue);
        setMasteredStats(prev => ({
          ...prev,
          [categoryValue]: {
            ...prev[categoryValue],
            manualMasteredWords: Object.keys(manualMastered),
            learnedMasteredWords: Object.keys(learnedMastered),
            manualMasteredCount: Object.keys(manualMastered).length,
            learnedMasteredCount: Object.keys(learnedMastered).length,
          }
        }));
        
        setSuccess(`单词 "${word}" 已释放`);
      }
    } catch (err) {
      console.error('释放单词失败:', err);
      setError('释放失败，请重试');
    } finally {
      // 结束加载
      setIsReleasing(false);
      setReleasingWord(null);
    }
  };

  // 生成分享图片
  const generateShareImage = async () => {
    setIsGeneratingImage(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 1200;
      canvas.height = 630;
      const ctx = canvas.getContext('2d');

      // 背景渐变
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(1, '#764ba2');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 添加装饰圆圈
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.beginPath();
      ctx.arc(100, 100, 150, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(1100, 530, 200, 0, Math.PI * 2);
      ctx.fill();

      // 标题
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 60px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('削词侠 学习成果', canvas.width / 2, 100);

      // 用户信息
      ctx.font = '32px Arial, sans-serif';
      ctx.fillText(`${userProfile.displayName || '学习者'}`, canvas.width / 2, 160);

      // 统计卡片背景
      const cardY = 220;
      const cardHeight = 280;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 20;
      ctx.shadowOffsetY = 10;
      ctx.fillRect(100, cardY, canvas.width - 200, cardHeight);
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;

      // 统计数据
      const stats = [
        { label: '总词汇量', value: totalWords, icon: '📚', color: '#667eea' },
        { label: '已掌握', value: totalMastered, icon: '🏆', color: '#f093fb' },
        { label: '掌握率', value: `${overallProgress}%`, icon: '🎯', color: '#4facfe' }
      ];

      const cardPadding = 80;
      const statWidth = (canvas.width - 200 - cardPadding * 2) / 3;
      
      stats.forEach((stat, index) => {
        const x = 100 + cardPadding + index * statWidth + statWidth / 2;
        const y = cardY + 80;

        // 图标
        ctx.font = '56px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(stat.icon, x, y);

        // 数值
        ctx.font = 'bold 48px Arial, sans-serif';
        ctx.fillStyle = stat.color;
        ctx.fillText(String(stat.value), x, y + 80);

        // 标签
        ctx.font = '24px Arial, sans-serif';
        ctx.fillStyle = '#666666';
        ctx.fillText(stat.label, x, y + 120);
      });

      // 网址
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('xiaocixia.com', canvas.width / 2, canvas.height - 40);

      // 转换为blob
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png');
      });
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // 分享到社交平台
  const shareToSocial = async (platform) => {
    try {
      const imageBlob = await generateShareImage();
      const imageUrl = URL.createObjectURL(imageBlob);
      
      const shareText = `我在削词侠已掌握${totalMastered}个单词，掌握率${overallProgress}%！一起来学习吧！`;
      const websiteUrl = 'https://xiaocixia.com';

      switch (platform) {
        case 'twitter': {
          // Twitter/X 文本分享
          window.open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(websiteUrl)}`,
            '_blank'
          );
          break;
        }
        
        case 'facebook': {
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(websiteUrl)}&quote=${encodeURIComponent(shareText)}`,
            '_blank'
          );
          break;
        }
        
        case 'download': {
          // 下载图片
          const link = document.createElement('a');
          link.href = imageUrl;
          link.download = `xiaocixia-progress-${Date.now()}.png`;
          link.click();
          URL.revokeObjectURL(imageUrl);
          break;
        }
        
        default: {
          // 其他平台使用Web Share API（如果支持）
          if (navigator.share && navigator.canShare) {
            const file = new File([imageBlob], 'xiaocixia-progress.png', { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                title: '削词侠学习成果',
                text: shareText,
                url: websiteUrl,
                files: [file]
              });
            } else {
              // 回退到只分享链接
              await navigator.share({
                title: '削词侠学习成果',
                text: shareText,
                url: websiteUrl
              });
            }
          } else {
            // 回退到下载
            const link = document.createElement('a');
            link.href = imageUrl;
            link.download = `xiaocixia-progress-${Date.now()}.png`;
            link.click();
          }
          URL.revokeObjectURL(imageUrl);
          break;
        }
      }
      
      setSuccess('分享图片已生成！');
    } catch (err) {
      console.error('分享失败:', err);
      setError('分享失败，请重试');
    }
  };

  const displayAvatar = previewUrl || userProfile.photoURL || 
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      userProfile.displayName || user.email.charAt(0)
    )}&size=100&background=1890ff&color=fff`;

  return (
    <div className="personal-center">
      <div className="personal-center-header">
        <button onClick={onBack} className="back-button">
          ← 返回学习
        </button>
        <h2>个人中心</h2>
      </div>
      
      {error && (
        <div className="message error-message">
          {error}
        </div>
      )}
      
      {success && (
        <div className="message success-message">
          ✓ {success}
        </div>
      )}

      {/* 个人等级卡片 */}
      <div className="user-level-card">
        <div className="level-badge">
          <div className="level-number">LV.{calculateLevel(totalMastered)}</div>
          <div className="level-title">{getLevelTitle(calculateLevel(totalMastered))}</div>
        </div>
        <div className="level-info">
          <div className="level-stat">
            <span className="stat-label">已掌握</span>
            <span className="stat-value">{totalMastered} 词</span>
          </div>
          <div className="level-stat">
            <span className="stat-label">当前排名</span>
            <span className="stat-value rank-highlight">
              {currentUserRank ? `第 ${currentUserRank} 名` : '未上榜'}
            </span>
          </div>
          <div className="level-stat">
            <span className="stat-label">下一级</span>
            <span className="stat-value">{100 - (totalMastered % 100)} 词</span>
          </div>
        </div>
        <div className="level-progress">
          <div className="level-progress-bar">
            <div 
              className="level-progress-fill" 
              style={{ width: `${(totalMastered % 100)}%` }}
            />
          </div>
          <div className="level-progress-text">
            {totalMastered % 100} / 100
          </div>
        </div>
      </div>

      {/* 排行榜 */}
      <div className="leaderboard-section">
        <h3>
          <span className="leaderboard-icon">🏆</span>
          学习排行榜
          <span className="leaderboard-subtitle">TOP 3</span>
          {/* <span className="leaderboard-subtitle">TOP 300</span> */}
        </h3>
        
        {isLoadingLeaderboard ? (
          <div className="leaderboard-loading">
            <div className="loading-spinner" />
            <p>加载排行榜中...</p>
          </div>
        ) : (
          <div className="leaderboard-list">
            {leaderboard.map((userItem, index) => {
              const isCurrentUser = userItem.userId === user.uid;
              const rank = index + 1;
              
              return (
                <div 
                  key={userItem.userId} 
                  className={`leaderboard-item ${isCurrentUser ? 'current-user' : ''} ${rank <= 3 ? 'top-three' : ''}`}
                >
                  <div className="rank-badge">
                    {rank === 1 && <span className="medal gold">🥇</span>}
                    {rank === 2 && <span className="medal silver">🥈</span>}
                    {rank === 3 && <span className="medal bronze">🥉</span>}
                    {rank > 3 && <span className="rank-number">{rank}</span>}
                  </div>
                  
                  <img 
                    src={userItem.photoURL} 
                    alt={userItem.displayName}
                    className="leaderboard-avatar"
                  />
                  
                  <div className="leaderboard-user-info">
                    <div className="user-name">
                      {userItem.displayName}
                      {isCurrentUser && <span className="you-badge">（你）</span>}
                    </div>
                    <div className="user-level">
                      Lv.{userItem.level} · {getLevelTitle(userItem.level)}
                    </div>
                  </div>
                  
                  <div className="leaderboard-stats">
                    <div className="mastered-count">{userItem.totalMastered}</div>
                    <div className="mastered-label">已掌握</div>
                  </div>
                </div>
              );
            })}
            
            {leaderboard.length === 0 && (
              <div className="leaderboard-empty">
                <p>暂无排行数据</p>
                <p className="empty-hint">开始学习，争夺排行榜首位！</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="main-content">
        <div className="profile-section">
          <div className="avatar-section">
            <img
              src={displayAvatar}
              alt="头像"
              className="avatar"
            />
            {previewUrl && (
              <p className="preview-text">预览新头像</p>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
              disabled={isUploading}
            />
          </div>
          
          <form onSubmit={handleUpdateProfile} className="profile-form">
            <div className="form-group">
              <label>昵称</label>
              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="输入昵称"
                className="form-input"
                disabled={isUploading}
              />
            </div>
            
            <div className="form-group">
              <label>邮箱</label>
              <input
                type="email"
                value={user.email}
                className="form-input disabled"
                disabled
              />
            </div>
            
            <button
              type="submit"
              disabled={isUploading || (!selectedFile && newDisplayName.trim() === userProfile.displayName)}
              className="save-button"
            >
              {isUploading ? (
                <>
                  <div className="loading-spinner" />
                  保存中...
                </>
              ) : (
                '保存更改'
              )}
            </button>
          </form>
        </div>

        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card total-words">
              <div className="stat-icon">📚</div>
              <div className="stat-content">
                <div className="stat-number">{totalWords}</div>
                <div className="stat-label">总词汇量</div>
              </div>
            </div>
            
            <div className="stat-card mastered-words">
              <div className="stat-icon">🏆</div>
              <div className="stat-content">
                <div className="stat-number">{totalMastered}</div>
                <div className="stat-label">已掌握</div>
              </div>
            </div>
            
            <div className="stat-card progress-rate">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-number">{overallProgress}%</div>
                <div className="stat-label">掌握率</div>
              </div>
            </div>
          </div>
          
          {/* 社交分享按钮 */}
          <div className="share-section" style={{ 
            marginTop: '30px', 
            padding: '20px', 
            background: '#f8f9fa', 
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <h4 style={{ 
              marginBottom: '15px', 
              color: '#333', 
              fontSize: '1.1em',
              textAlign: 'center'
            }}>
              分享学习成果
            </h4>
            <div className="share-buttons" style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => shareToSocial('twitter')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#1DA1F2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="分享到 Twitter/X"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Twitter/X
              </button>
              
              <button
                onClick={() => shareToSocial('facebook')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#1877F2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="分享到 Facebook"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>
              
              <button
                onClick={() => shareToSocial('download')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="下载图片"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
                </svg>
                下载图片
              </button>
              
              <button
                onClick={() => shareToSocial('share')}
                disabled={isGeneratingImage}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 20px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isGeneratingImage ? 'not-allowed' : 'pointer',
                  fontSize: '0.9em',
                  opacity: isGeneratingImage ? 0.6 : 1,
                  transition: 'all 0.3s'
                }}
                title="更多分享选项"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
                </svg>
                更多
              </button>
            </div>
            {isGeneratingImage && (
              <p style={{ 
                marginTop: '10px', 
                color: '#666', 
                fontSize: '0.9em',
                textAlign: 'center'
              }}>
                正在生成分享图片...
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="progress-section">
        <h3>学习进度详情</h3>
        <div className="categories-grid">
          {categories.map((cat) => {
            const stats = masteredStats[cat.value] || {};
            
            // 只统计手动削词
            const manualWords = new Set(stats.manualMasteredWords || []);
            const actualMasteredCount = manualWords.size;
            
            // 修正进度计算
            const progress = stats.totalWords > 0 
              ? Math.min(100, Math.round((actualMasteredCount / stats.totalWords) * 100))
              : 0;
            
            const isExpanded = expandedCategory === cat.value;
            
            return (
              <div key={cat.value} className="category-card">
                <div className="category-header">
                  <h4>{cat.label}</h4>
                  <div className="category-actions">
                    <button
                      onClick={() => setExpandedCategory(isExpanded ? null : cat.value)}
                      className="expand-button"
                    >
                      {isExpanded ? '收起' : '展开'}
                    </button>
                    <button
                      onClick={(e) => handleResetCategory(cat.value, e)}
                      className="reset-button"
                      type="button"
                      disabled={isResetting}
                    >
                      {isResetting && resettingCategory === cat.value ? (
                        <>
                          <div className="mini-spinner" style={{ marginRight: '5px' }}></div>
                          重置中...
                        </>
                      ) : (
                        '重置'
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="category-stats">
                  <div className="stat-item">
                    <span className="stat-value">{completedCounts[cat.value] || 0}</span>
                    <span className="stat-desc">学习次数</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{manualWords.size}</span>
                    <span className="stat-desc">已掌握</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{stats.totalWords || 0}</span>
                    <span className="stat-desc">总单词数</span>
                  </div>
                </div>
                
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <div className="progress-text">{progress}% 完成</div>

                {/* 👇 添加这部分展开内容 */}
                {isExpanded && (
                  <div className="expanded-words">
                    {/* 手动削词列表 */}
                    {manualWords.size > 0 && (
                      <div className="words-section">
                        <h5 className="words-title">
                          🏆 已掌握单词 ({manualWords.size})
                        </h5>
                        <div className="words-list">
                          {Array.from(manualWords).map((word, index) => {
                            const wordKey = `${cat.value}-${word}`;
                            const isReleasingThis = isReleasing && releasingWord === wordKey;
                            
                            return (
                              <span key={`manual-${index}`} className="word-tag manual">
                                {word}
                                {isReleasingThis ? (
                                  <div className="word-releasing-spinner" title="释放中...">
                                    <div className="mini-spinner"></div>
                                  </div>
                                ) : (
                                  <button
                                    className="word-release-btn"
                                    onClick={() => releaseWord(cat.value, word, 'manual')}
                                    title="释放该单词"
                                    disabled={isReleasing}
                                  >
                                    ×
                                  </button>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* 学习掌握列表 */}
                    {manualWords.size === 0 && (
                      <div className="no-words">
                        <p>该级别还没有掌握的单词</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PersonalCenter;